import { type ReactNode } from 'react'

export function AuthShell({
	eyebrow,
	title,
	intro,
	children,
	footer,
	width = 'md',
}: {
	eyebrow: string
	title: ReactNode
	intro?: ReactNode
	children: ReactNode
	footer?: ReactNode
	width?: 'sm' | 'md' | 'lg'
}) {
	const maxWidth =
		width === 'sm' ? 'max-w-sm' : width === 'lg' ? 'max-w-xl' : 'max-w-md'
	return (
		// A `<div>`, not a `<main>`: MainLayout already owns the page's single
		// `<main id="main-content">` and the skip link targets it, so nesting a
		// second one here put the auth card outside the landmark the skip link
		// lands in and left the document with two main regions.
		// `overflow-hidden` because the glow below is deliberately hung off the
		// right edge, and without a clip it extended the document 15% past the
		// viewport at every width - 216px of horizontal scroll at 1440px, on every
		// page this shell renders.
		<div className="dark relative isolate flex min-h-[calc(100dvh-3.5rem)] flex-col overflow-hidden bg-[var(--marketing-base)] text-[var(--marketing-black)]">
			<div
				aria-hidden
				className="pointer-events-none absolute -top-32 right-[-15%] h-[420px] w-[520px] opacity-30"
				style={{
					background:
						'radial-gradient(closest-side, rgba(103,232,249,0.22), rgba(103,232,249,0) 70%)',
				}}
			/>
			<div aria-hidden className="grain" />

			<div
				className={`relative z-10 mx-auto flex w-full ${maxWidth} flex-1 flex-col px-6 pt-16 pb-16 sm:pt-24`}
			>
				<header className="mb-10 text-left">
					<p className="eyebrow text-[var(--marketing-faint)]">{eyebrow}</p>
					<h1
						className="mt-3 font-[family-name:var(--font-display)] text-[38px] leading-[1.05] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[44px]"
						style={{ textWrap: 'balance' }}
					>
						{title}
					</h1>
					{intro ? (
						<p
							className="mt-4 max-w-md text-[14px] leading-relaxed text-[var(--marketing-gray)]"
							style={{ textWrap: 'pretty' }}
						>
							{intro}
						</p>
					) : null}
				</header>

				<div className="marketing-card relative p-7 sm:p-8">{children}</div>

				{footer ? (
					<div className="mt-8 text-[13px] text-[var(--marketing-gray)]">
						{footer}
					</div>
				) : null}
			</div>
		</div>
	)
}

export function AuthField({
	label,
	id,
	error,
	hint,
	children,
}: {
	label: string
	id: string
	error?: string | null
	hint?: ReactNode
	children: ReactNode
}) {
	const errorId = `${id}-error`
	return (
		<div className="mb-5 last:mb-0">
			<div className="mb-1.5 flex items-baseline justify-between gap-3">
				<label
					htmlFor={id}
					className="font-[family-name:var(--font-mono)] text-[11px] tracking-[0.16em] text-[var(--marketing-faint)] uppercase"
				>
					{label}
				</label>
				{hint ? (
					<span className="text-[12px] text-[var(--marketing-gray)]">
						{hint}
					</span>
				) : null}
			</div>
			{children}
			{error ? (
				<p
					id={errorId}
					className="mt-2 font-[family-name:var(--font-mono)] text-[11px] text-[var(--marketing-danger)]"
				>
					{error}
				</p>
			) : null}
		</div>
	)
}

export function AuthDivider({ children }: { children: ReactNode }) {
	return (
		<div className="my-7 flex items-center gap-3">
			<span className="h-px flex-1 bg-[var(--marketing-border)]" />
			<span className="font-[family-name:var(--font-mono)] text-[10.5px] tracking-[0.18em] text-[var(--marketing-faint)] uppercase">
				{children}
			</span>
			<span className="h-px flex-1 bg-[var(--marketing-border)]" />
		</div>
	)
}

export function AuthSubmit({
	isPending,
	pendingLabel,
	children,
}: {
	isPending: boolean
	pendingLabel: string
	children: ReactNode
}) {
	return (
		<button
			type="submit"
			disabled={isPending}
			className="btn-marketing mt-2 w-full disabled:cursor-not-allowed disabled:opacity-60"
		>
			<span className="flex items-center justify-center gap-2">
				{isPending ? (
					<>
						<Spinner />
						{pendingLabel}
					</>
				) : (
					<>
						{children}
						<span aria-hidden>→</span>
					</>
				)}
			</span>
		</button>
	)
}

function Spinner() {
	return (
		<svg
			className="h-4 w-4 animate-spin"
			viewBox="0 0 24 24"
			fill="none"
			aria-hidden
		>
			<circle
				cx="12"
				cy="12"
				r="10"
				stroke="currentColor"
				strokeOpacity="0.25"
				strokeWidth="3"
			/>
			<path
				d="M22 12a10 10 0 0 1-10 10"
				stroke="currentColor"
				strokeWidth="3"
				strokeLinecap="round"
			/>
		</svg>
	)
}
